import { Controller } from "@hotwired/stimulus"

// Reloads the current page on an interval while it is connected to the DOM.
// Intended for "work in progress" pages (e.g. a background analysis): render
// this controller only while the work is pending/processing, and it stops
// itself once the server re-renders without it.
//
// Given a frame, it reloads only that <turbo-frame> from `url`, leaving the rest
// of the page (open tab, scroll position) alone. Render it inside the frame so a
// reload without it stops the polling.
//
// Usage:
//   <div data-controller="auto-refresh" data-auto-refresh-interval-value="4000">
//   <div data-controller="auto-refresh" data-auto-refresh-frame-value="summary_frame"
//        data-auto-refresh-url-value="/path/to/frame/content" hidden>
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 4000 }, // ms between reloads
    frame: String, // optional id of a <turbo-frame> to reload instead of the page
    url: String // that frame's source until it has one of its own
  }

  connect() {
    this.timer = setInterval(() => this.refresh(), this.intervalValue)
  }

  disconnect() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  refresh() {
    // Don't reload a tab the user isn't looking at — wait until it's visible.
    if (document.hidden) return

    if (this.hasFrameValue) {
      this.refreshFrame()
    } else if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  // A reload cancels the frame's in-flight fetch, so a response slower than the
  // interval would never land; skip ticks until the current one finishes.
  refreshFrame() {
    const frame = document.getElementById(this.frameValue)
    if (!frame || frame.hasAttribute("busy")) return

    if (frame.getAttribute("src")) {
      frame.reload()
    } else {
      frame.src = this.urlValue
    }
  }
}
